import React from 'react';
import { TFunction } from 'i18next';
import { VisitDtoModelType } from '../../../../store/mst/models';
import DurationRow from './DurationRow';
import TimeWindows from '../../../shared/TimeWindows';
import DeliveryDate from './DeliveryDate';
import PackagesSection from './PackagesSection';
import { TagsRow } from '../../../shared/Tags';
import { TimelineViolationTypes } from '../../../../helpers/lateness';
import OrderWarning, { OrderWarningType } from '../../OrderWarning';
import { OrderDetailsCardRow } from './DeliveryDetails';
import { CollapsibleNote } from '../../../shared/DetailsCard';
import { withFromCustomer } from '../../../customerProfiles/FromCustomerMark';
import { customField, FilledField, getFilledFields } from '../../../customerProfiles/customerFilledFields';

const orderDetails = (t: TFunction, visit: VisitDtoModelType): OrderDetailsCardRow[] => {
  const filled = getFilledFields(visit);
  const mark = (field: FilledField, value: React.ReactNode, warned = false) => withFromCustomer(
    value,
    !!value && !warned && filled.includes(field),
  );
  const violations = visit.timeline?.violations ?? [];
  const tagMismatch = violations.some((v) => v.type === TimelineViolationTypes.TAG_MISMATCH);
  const late = violations.some((v) => v.type === TimelineViolationTypes.LATENESS || v.type === TimelineViolationTypes.EARLINESS);
  const instructions = visit.instructions?.trim();
  const notesTwo = visit.notes2;
  const noTimeWindows = !visit.timeWindows?.length;
  const duration = `${visit.durationMinutes} ${t('orderDetails.durationSuffix')}`;
  const load = visit.load === null ? '' : `${visit.load} ${t('orderDetails.loadSuffix')}`;

  const packages = visit.packages?.slice() || [];
  const orderAttempt = visit.attempt();
  const scannedPackages = orderAttempt?.scannedPackages?.slice() || [];

  return [
    {
      label: t('orderDetails.customerOrderNumber'),
      value: visit.customerOrderNumber ? visit.customerOrderNumber : undefined,
    },
    {
      label: t('orderDetails.tags'),
      value: mark('tags', visit.tagNames?.length ? (
        <TagsRow
          tags={visit.tagNames}
          prefix={tagMismatch ? <OrderWarning types={[OrderWarningType.TAG_MISMATCH]} /> : undefined}
        />
      ) : undefined, tagMismatch),
    },
    {
      label: t('orderDetails.deliveryDate'),
      value: visit.deliveryDate ? <DeliveryDate deliveryDate={visit.deliveryDate} isDateMismatched={visit.isDateMismatched ?? false} /> : undefined,
    },
    {
      label: t('orderDetails.deliverBetween.title'),
      value: mark('timeWindow', noTimeWindows ? undefined : <TimeWindows visit={visit} showLateness={true} />, late),
    },
    {
      label: t('orderDetails.duration'),
      value: mark('duration', duration ? <DurationRow value={duration} /> : undefined),
    },
    {
      label: t('orderDetails.load'),
      value: mark('load', load || undefined),
    },
    {
      label: t('orderDetails.packages'),
      value: packages.length > 0 ? <PackagesSection packages={packages} scannedPackages={scannedPackages} /> : undefined,
    },
    {
      label: t('orderDetails.instructions'),
      value: mark('instructions', instructions ? <CollapsibleNote value={instructions} /> : undefined),
    },
    {
      label: t('orderDetails.noteTwo'),
      value: mark(customField('notes2'), notesTwo ? <CollapsibleNote value={notesTwo} /> : undefined),
    },
    ...(visit.customFieldsList || [])
      .filter((field) => !!field.value?.trim())
      .map((field) => ({
        label: field.key,
        value: mark(customField(field.key), <CollapsibleNote value={field.value} />),
      })),
    {
      label: t('orderDetails.routificOrderNumber'),
      value: visit.routificOrderNumber,
    },
  ];
};

export default orderDetails;
